import { createReducer, on } from '@ngrx/store';
import { createDefaultILoadableArray, createDefaultLoadable, onLoadableError, onLoadableLoad, onLoadableSuccess, toLoadableWrapper } from '../../models/loadable';
import { IFinding } from '../../models/detection.interface';
import { IFindingsPage, IJobResults, ISpotlyteJobDetail } from '../../models/spotlyte.interface';
import { ILoadableArray } from '../../models/loadable';
import { initialExportState, initialResultBlobs } from './spotlyte.state';
import { UserActions } from '../user/user.actions';
import { SpotlyteActions } from './spotlyte.actions';
import { initialSpotlyteState } from './spotlyte.state';

export const spotlyteReducer = createReducer(
    initialSpotlyteState,

    // Sources
    on(SpotlyteActions.loadSources, state => ({
        ...state,
        sources: onLoadableLoad({ ...state.sources }),
    })),
    on(SpotlyteActions.loadSourcesSuccess, (state, { sources }) => ({
        ...state,
        sources: onLoadableSuccess({ ...state.sources, items: sources }),
    })),
    on(SpotlyteActions.loadSourcesFailure, (state, { error }) => ({
        ...state,
        sources: onLoadableError({ ...state.sources }, error),
    })),

    on(SpotlyteActions.deleteSources, state => ({
        ...state,
        sources: onLoadableLoad({ ...state.sources }),
    })),
    on(SpotlyteActions.deleteSourcesSuccess, (state, { deleted }) => {
        const deletedIds = new Set(deleted.map(s => s.id));
        return {
            ...state,
            sources: onLoadableSuccess({
                ...state.sources,
                items: state.sources.items.filter(s => !deletedIds.has(s.id)),
            }),
        };
    }),
    on(SpotlyteActions.deleteSourcesFailure, (state, { error }) => ({
        ...state,
        sources: onLoadableError({ ...state.sources }, error),
    })),

    on(SpotlyteActions.completeUploadSuccess, (state, { source }) => ({
        ...state,
        sources: onLoadableSuccess({
            ...state.sources,
            items: [...state.sources.items, source],
        }),
    })),

    on(SpotlyteActions.createVmsSourcesSuccess, (state, { sources }) => ({
        ...state,
        sources: onLoadableSuccess({
            ...state.sources,
            items: [...state.sources.items, ...sources],
        }),
    })),

    // Jobs
    on(SpotlyteActions.loadJobs, state => ({
        ...state,
        jobs: onLoadableLoad({ ...state.jobs }),
    })),
    on(SpotlyteActions.loadJobsSuccess, (state, { jobs }) => ({
        ...state,
        jobs: onLoadableSuccess({ ...state.jobs, items: jobs }),
    })),
    on(SpotlyteActions.loadJobsFailure, (state, { error }) => ({
        ...state,
        jobs: onLoadableError({ ...state.jobs }, error),
    })),

    on(SpotlyteActions.loadJob, state => ({
        ...state,
        selectedJob: onLoadableLoad(state.selectedJob ?? toLoadableWrapper({} as ISpotlyteJobDetail)),
    })),
    on(SpotlyteActions.loadJobSuccess, (state, { job }) => ({
        ...state,
        selectedJob: onLoadableSuccess(toLoadableWrapper(job)),
    })),
    on(SpotlyteActions.loadJobFailure, (state, { error }) => ({
        ...state,
        selectedJob: onLoadableError(
            state.selectedJob ?? toLoadableWrapper({} as ISpotlyteJobDetail),
            error,
        ),
    })),
    on(SpotlyteActions.clearSelectedJob, state => ({
        ...state,
        selectedJob: null,
    })),

    // Job Results
    on(SpotlyteActions.loadResults, state => ({
        ...state,
        jobResults: onLoadableLoad(state.jobResults ?? toLoadableWrapper({} as IJobResults)),
    })),
    on(SpotlyteActions.loadResultsSuccess, (state, { results }) => ({
        ...state,
        jobResults: onLoadableSuccess(toLoadableWrapper(results)),
        resultBlobs: {
            ...initialResultBlobs,
            loading: true,
        },
    })),
    on(SpotlyteActions.loadResultsFailure, (state, { error }) => ({
        ...state,
        jobResults: onLoadableError(
            state.jobResults ?? toLoadableWrapper({} as IJobResults),
            error,
        ),
    })),
    on(SpotlyteActions.clearResults, state => ({
        ...state,
        jobResults: null,
        resultBlobs: initialResultBlobs,
    })),

    on(SpotlyteActions.loadResultBlobsSuccess, (state, { summary, charts, zones }) => ({
        ...state,
        resultBlobs: {
            loading: false,
            error: false,
            summary,
            charts,
            zones,
        },
    })),
    on(SpotlyteActions.loadResultBlobsFailure, state => ({
        ...state,
        resultBlobs: {
            ...initialResultBlobs,
            error: true,
        },
    })),

    on(SpotlyteActions.createJob, state => ({
        ...state,
        jobs: onLoadableLoad({ ...state.jobs }),
    })),
    on(SpotlyteActions.createJobSuccess, (state, { job }) => ({
        ...state,
        jobs: onLoadableSuccess({
            ...state.jobs,
            items: [job, ...state.jobs.items],
        }),
    })),
    on(SpotlyteActions.createJobFailure, (state, { error }) => ({
        ...state,
        jobs: onLoadableError({ ...state.jobs }, error),
    })),

    on(SpotlyteActions.cancelJobs, state => ({
        ...state,
        jobs: onLoadableLoad({ ...state.jobs }),
    })),
    on(SpotlyteActions.cancelJobsSuccess, (state, { cancelled }) => {
        const cancelledMap = new Map(cancelled.map(j => [j.id, j]));
        return {
            ...state,
            jobs: onLoadableSuccess({
                ...state.jobs,
                items: state.jobs.items.map(j => cancelledMap.get(j.id) ?? j),
            }),
        };
    }),
    on(SpotlyteActions.cancelJobsFailure, (state, { error }) => ({
        ...state,
        jobs: onLoadableError({ ...state.jobs }, error),
    })),

    // Findings
    on(SpotlyteActions.loadFindings, state => ({
        ...state,
        findings: onLoadableLoad(state.findings ?? toLoadableWrapper({} as IFindingsPage)),
    })),
    on(SpotlyteActions.loadFindingsSuccess, (state, { page }) => ({
        ...state,
        findings: onLoadableSuccess(toLoadableWrapper(page)),
        findingsData: onLoadableLoad({ ...state.findingsData }),
    })),
    on(SpotlyteActions.loadFindingsFailure, (state, { error }) => ({
        ...state,
        findings: onLoadableError(
            state.findings ?? toLoadableWrapper({} as IFindingsPage),
            error,
        ),
    })),
    on(SpotlyteActions.loadFindingsBlobSuccess, (state, { findings }) => ({
        ...state,
        findingsData: onLoadableSuccess({ ...state.findingsData, items: findings }),
    })),
    on(SpotlyteActions.loadFindingsBlobFailure, (state, { error }) => ({
        ...state,
        findingsData: onLoadableError({ ...state.findingsData }, error),
    })),
    on(SpotlyteActions.clearFindings, state => ({
        ...state,
        findings: null,
        findingsData: createDefaultILoadableArray() as ILoadableArray<IFinding>,
    })),

    // Export
    on(SpotlyteActions.exportCsv, state => ({
        ...state,
        csvExport: { loading: true, error: false },
    })),
    on(SpotlyteActions.exportCsvSuccess, state => ({
        ...state,
        csvExport: initialExportState,
    })),
    on(SpotlyteActions.exportCsvFailure, state => ({
        ...state,
        csvExport: { loading: false, error: true },
    })),

    on(SpotlyteActions.exportPdf, state => ({
        ...state,
        pdfExport: { loading: true, error: false },
    })),
    on(SpotlyteActions.exportPdfSuccess, state => ({
        ...state,
        pdfExport: initialExportState,
    })),
    on(SpotlyteActions.exportPdfFailure, state => ({
        ...state,
        pdfExport: { loading: false, error: true },
    })),

    // QnA
    on(SpotlyteActions.loadQnaHistory, state => ({
        ...state,
        qnaHistory: onLoadableLoad({ ...state.qnaHistory }),
    })),
    on(SpotlyteActions.loadQnaHistorySuccess, (state, { turns }) => ({
        ...state,
        qnaHistory: onLoadableSuccess({ ...state.qnaHistory, items: turns }),
    })),
    on(SpotlyteActions.loadQnaHistoryFailure, (state, { error }) => ({
        ...state,
        qnaHistory: onLoadableError({ ...state.qnaHistory }, error),
    })),

    on(SpotlyteActions.askQuestion, state => ({
        ...state,
        qnaAsk: onLoadableLoad({ ...state.qnaAsk }),
    })),
    on(SpotlyteActions.askQuestionSuccess, (state, { turn }) => ({
        ...state,
        qnaAsk: onLoadableSuccess({ ...state.qnaAsk }),
        qnaHistory: {
            ...state.qnaHistory,
            items: [...state.qnaHistory.items, turn],
        },
    })),
    on(SpotlyteActions.askQuestionFailure, (state, { error }) => ({
        ...state,
        qnaAsk: onLoadableError({ ...state.qnaAsk }, error),
    })),
    on(SpotlyteActions.clearQnaHistory, state => ({
        ...state,
        qnaHistory: { ...initialSpotlyteState.qnaHistory },
        qnaAsk: createDefaultLoadable(),
    })),

    on(UserActions.logout, _ => ({ ...initialSpotlyteState })),
);
